/* eslint-disable camelcase */
import { FlowSpec } from '@digibee/flow';
import { isAxiosError } from 'axios';

import type {
  ChatHistoryParams,
  ChatHistoryResponse,
  ChatResumeParams,
  ChatResumeResponse,
  SendChatMessageParams2
} from './types';

import axiosInstance from '~/common/helpers/axiosInstance';

type GetNextComponentParams = {
  realm?: string;
  triggerName?: string;
  sequence?: string[];
  params?: {
    trigger?: string;
    connectors?: string[];
    isTargetOf?: string;
    isOtherwiseOf?: string;
    isOnProcessOf?: string;
    isOnExceptionOf?: string;
    pipelineEndpointId?: string;
    flowspec?: FlowSpec;
  };
  token: string;
};

type ExecutionMessages = {
  id: string;
  body: string;
  processorStepName: string;
  processorType: string;
  elapsed: number;
  connectorId: string;
  connectorName: string;
  timestampStart: number;
  timestampEnd: number;
  indexes: object;
};

type ExecutionLogs = {
  id: string;
  pipelineName: string;
  logMessage: string;
  timestamp: string;
  loglevel: string;
};

type SendFlowToInsightsParams = {
  flowSpec: FlowSpec;
  executionMessages?: ExecutionMessages[];
  executionLogs?: ExecutionLogs[];
  executionInput?: object;
  executionOutput?: object;
  realm: string;
  token?: string;
};

type NextComponentSuggestion = {
  class: string;
  component_name: string;
  probability: number;
};

export const chatDesignAIAssistantMethods = {
  sendAIChatMessage: async ({
    lang,
    message,
    realm,
    target,
    history
  }: SendChatMessageParams2) => {
    const timeout = 1000 * 58; // 58 seconds
    try {
      const body = {
        message,
        history
      };

      const qs = new URLSearchParams({
        target: String(target),
        lang: String(lang)
      }).toString();

      const { data } = await axiosInstance.post(
        `design/realms/${encodeURIComponent(realm)}/ai-services/ai-chat/message?${qs}`,
        body,
        { timeout }
      );

      return data;
    } catch (error) {
      if (error instanceof Error && isAxiosError(error)) {
        const isTimeoutFromGraph =
          error.message === `timeout of ${timeout}ms exceeded`;

        if (isTimeoutFromGraph) {
          throw new Error(
            JSON.stringify({
              data: {
                status: 502,
                timeout,
                message: 'Timeout'
              }
            })
          );
        }

        throw new Error(error?.response?.data);
      }
      throw error;
    }
  },

  getNextComponentSuggestions: async ({
    realm,
    triggerName,
    sequence
  }: GetNextComponentParams) => {
    try {
      const body =
        sequence && sequence.length > 0
          ? { sequence }
          : { trigger: triggerName };

      const response = await axiosInstance.post(
        `design/realms/${encodeURIComponent(realm ?? '')}/ai-services/component-prediction`,
        body
      );

      const suggestions: NextComponentSuggestion[] = response.data.result.map(
        (suggestion: NextComponentSuggestion) => ({
          ...suggestion,
          class: suggestion.class || suggestion.component_name
        })
      );

      return suggestions;
    } catch (error) {
      if (error instanceof Error && isAxiosError(error))
        throw Error(error.message);
      throw error;
    }
  },
  sendFlowToInsights: async ({
    flowSpec,
    executionMessages,
    executionLogs,
    executionInput,
    executionOutput,
    realm
  }: SendFlowToInsightsParams) => {
    try {
      const body = {
        flowspec: flowSpec,
        executionMessages: executionMessages || [],
        executionLogs: executionLogs || [],
        executionInput: executionInput || {},
        executionOutput: executionOutput || {}
      };
      const { data } = await axiosInstance.post(
        `design/realms/${encodeURIComponent(realm)}/ai-services/ai-insights`,
        body
      );

      if (!data.insights) {
        return null;
      }
      return data;
    } catch (error) {
      if (error instanceof Error && isAxiosError(error))
        throw Error(error.message);
      throw error;
    }
  },
  chatHistory: async ({
    context,
    isGroupedByConversation,
    pipeline,
    realm,
    user
  }: ChatHistoryParams) => {
    try {
      const body = {
        realm,
        user,
        context,
        pipeline,
        is_grouped_by_conversation: isGroupedByConversation
      };

      const { data } = await axiosInstance.post(
        `design/realms/${encodeURIComponent(realm)}/ai-services/chat-history`,
        body
      );

      return data as ChatHistoryResponse;
    } catch (error) {
      if (error instanceof Error && isAxiosError(error))
        throw Error(error.message);
      throw error;
    }
  },
  chatResume: async ({
    context,
    pipeline,
    realm,
    token,
    content,
    user,
    actionResult,
    autoNextState,
    uiComponents,
    contextInfo
  }: ChatResumeParams) => {
    const params = {
      context,
      pipeline,
      realm,
      token,
      content,
      user,
      actionResult,
      autoNextState,
      uiComponents,
      contextInfo
    };
    try {
      const body = {
        realm,
        user,
        content,
        context,
        pipeline,
        action_result: actionResult,
        auto_next_state: autoNextState,
        ui_components: uiComponents,
        context_info: contextInfo
      };

      const { data } = await axiosInstance.post(
        `design/realms/${encodeURIComponent(realm)}/ai-services/chat-resume`,
        body
      );

      return { ...data, lastParams: params } as ChatResumeResponse;
    } catch (error) {
      if (error instanceof Error && isAxiosError(error))
        throw Error(error.message);
      throw error;
    }
  }
};
